/**
 * @file DataGrid.tsx
 * @description Reusable DataGrid system built on top of AppTable.
 *
 * Adds:
 * - Typed column config with cell types (text, currency, date, badge)
 * - Inline editing (double-click to edit, save on blur/enter)
 * - Integrated toolbar (search, filters, columns, views)
 * - Memoized rows to avoid full re-renders
 *
 * GOVERNED BY: copilot-instructions.md – Sections 7, 23 (UI Standards)
 */

import { memo, useMemo, useCallback, useState, Fragment } from 'react';
import { cn } from '@/lib/utils';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '../../button';
import { TableSkeleton } from '../../AppSkeleton';
import { AppTableToolbar } from '../AppTableToolbar';
import { DataGridEditableCell } from './DataGridEditableCell';
import { resolveColumns } from './columnResolver';
import type {
  DataGridProps,
  DataGridColumn,
  DataGridCellType,
  DataGridCardViewConfig,
} from './dataGridTypes';

// ─────────────────────────────────────────────────────────────
// Cell Formatters
// ─────────────────────────────────────────────────────────────

function formatCurrencyValue(value: unknown, config?: { currency?: string; locale?: string }): string {
  const num = Number(value);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat(config?.locale ?? 'en-US', {
    style: 'currency',
    currency: config?.currency ?? 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDateValue(value: unknown, config?: { format?: string; locale?: string }): string {
  if (!value) return '—';
  const date = new Date(String(value));
  if (isNaN(date.getTime())) return String(value);

  const locale = config?.locale ?? 'en-US';
  const fmt = config?.format ?? 'medium';

  const options: Intl.DateTimeFormatOptions =
    fmt === 'short' ? { month: 'numeric', day: 'numeric', year: '2-digit' }
    : fmt === 'long' ? { month: 'long', day: 'numeric', year: 'numeric' }
    : fmt === 'iso' ? undefined as unknown as Intl.DateTimeFormatOptions
    : { month: 'short', day: 'numeric', year: 'numeric' };

  if (fmt === 'iso') return date.toISOString().slice(0, 10);
  return date.toLocaleDateString(locale, options);
}

function BadgeCell({ value, variantMap }: { value: unknown; variantMap?: Record<string, string> }) {
  const str = String(value ?? '');
  if (!str) return <span>—</span>;

  const variant = variantMap?.[str] ?? 'default';
  const variantClasses: Record<string, string> = {
    default: 'bg-info/10 text-info',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    muted: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300',
    info: 'bg-info/10 text-info',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
        variantClasses[variant] ?? variantClasses.default,
      )}
    >
      {str}
    </span>
  );
}

function formatCellValue<TRow>(
  value: unknown,
  type?: DataGridCellType,
  column?: DataGridColumn<TRow>,
): React.ReactNode {
  if (value == null || value === '') return <span className="text-muted-foreground">—</span>;

  switch (type) {
    case 'currency':
      return formatCurrencyValue(value, column?.currencyConfig);
    case 'date':
      return formatDateValue(value, column?.dateConfig);
    case 'badge':
      return <BadgeCell value={value} variantMap={column?.badgeConfig?.variantMap} />;
    case 'text':
    default:
      return String(value);
  }
}

// ─────────────────────────────────────────────────────────────
// Responsive Card (md:hidden row replacement)
// ─────────────────────────────────────────────────────────────

/** Finds a column's rendered cell content, reusing the same render()/formatCellValue
 *  path the table uses for that column — a card field is never computed differently
 *  from its table-cell counterpart. */
function renderColumnContent<TRow>(
  columns: DataGridColumn<TRow>[],
  key: keyof TRow | string,
  row: TRow,
  rowIndex: number,
): { label: string; content: React.ReactNode } | null {
  const col = columns.find((c) => String(c.key) === String(key));
  if (!col) return null; // column not visible (RBAC/visibility rules) — omit from card too
  const content = col.render
    ? col.render(row, rowIndex)
    : formatCellValue(getCellValueFromRow(row, col.key), col.type, col);
  return { label: col.label, content };
}

interface DataGridCardProps<TRow> {
  row: TRow;
  rowIndex: number;
  columns: DataGridColumn<TRow>[];
  cardView: DataGridCardViewConfig<TRow>;
  onRowClick?: (row: TRow, index: number) => void;
  rowClassName?: string;
  /** Mirrors the table's expanded-row support: when the row is expanded,
   *  the SAME renderExpandedRow output is shown below the card. */
  expandedContent?: React.ReactNode;
}

function DataGridCard<TRow>({
  row,
  rowIndex,
  columns,
  cardView,
  onRowClick,
  rowClassName,
  expandedContent,
}: DataGridCardProps<TRow>) {
  const leading = cardView.leadingKey
    ? renderColumnContent(columns, cardView.leadingKey, row, rowIndex)
    : null;
  const title = renderColumnContent(columns, cardView.titleKey, row, rowIndex);
  const fields = cardView.fieldKeys
    .map((key) => renderColumnContent(columns, key, row, rowIndex))
    .filter((f): f is NonNullable<typeof f> => f !== null);
  const secondary = renderColumnContent(
    columns,
    cardView.secondaryActionsKey ?? 'actions',
    row,
    rowIndex,
  );
  const primary = cardView.primaryAction?.(row, rowIndex);

  const handleClick = (e: React.MouseEvent) => {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('[role="button"]') ||
      target.closest('[role="menu"]')
    ) {
      return;
    }
    onRowClick(row, rowIndex);
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card p-3',
        onRowClick && 'cursor-pointer active:bg-muted/30',
        rowClassName,
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {leading?.content && <div className="flex-shrink-0">{leading.content}</div>}
          <div className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">
            {title?.content}
          </div>
        </div>
        {secondary?.content && <div className="flex-shrink-0">{secondary.content}</div>}
      </div>

      {fields.length > 0 && (
        <dl className="mt-2 flex flex-col gap-1.5 border-t border-border/60 pt-2">
          {fields.map(({ label, content }, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3 text-[12px]">
              <dt className="flex-shrink-0 text-muted-foreground">{label}</dt>
              <dd className="min-w-0 text-right text-foreground">{content}</dd>
            </div>
          ))}
        </dl>
      )}

      {primary && <div className="mt-3">{primary}</div>}

      {expandedContent && (
        <div className="mt-3 border-t border-border/60 pt-3" onClick={(e) => e.stopPropagation()}>
          {expandedContent}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DataGrid Component
// ─────────────────────────────────────────────────────────────

function DataGridInner<TRow>({
  columns,
  data,
  editable = false,
  onUpdate,
  getRowKey,
  loading = false,
  emptyTitle = 'No data available',
  emptyDescription,
  emptyContent,
  error,
  sort,
  onSortChange,
  pagination,
  visibleColumns,
  onRowClick,
  toolbar,
  searchValue,
  onSearchChange,
  compact = false,
  density,
  striped = false,
  stickyHeader = false,
  getRowClassName,
  renderExpandedRow,
  isRowExpanded,
  skeletonRows = 6,
  className,
  testId,
  resolverContext,
  programOverrides,
  cardView,
}: DataGridProps<TRow>) {
  const isCompact = compact || density === 'compact';

  // ── Resolve columns (visibility + editability rules) ───────
  const resolvedCols = useMemo(() => {
    if (!resolverContext) return columns;
    return resolveColumns(columns, resolverContext, { programOverrides });
  }, [columns, resolverContext, programOverrides]);

  // ── Visible columns ────────────────────────────────────────
  const visibleCols = useMemo(() => {
    // First filter by `visible` prop on column itself
    let cols = resolvedCols.filter((col) => col.visible !== false);
    // Then filter by visibleColumns state if provided
    if (visibleColumns) {
      cols = cols.filter((col) => {
        const key = String(col.key);
        return visibleColumns[key] !== false;
      });
    }
    return cols;
  }, [resolvedCols, visibleColumns]);

  // ── Internal sort state (used when sort/onSortChange not provided) ──
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>('asc');

  const effectiveSort = sort ?? (internalSortKey ? { column: internalSortKey, direction: internalSortDir } : undefined);

  // ── Sort handler ───────────────────────────────────────────
  const handleSort = useCallback(
    (key: string) => {
      if (onSortChange) {
        // Controlled mode
        if (sort?.column === key) {
          onSortChange({ column: key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
        } else {
          onSortChange({ column: key, direction: 'asc' });
        }
      } else {
        // Uncontrolled mode
        if (internalSortKey === key) {
          setInternalSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
          setInternalSortKey(key);
          setInternalSortDir('asc');
        }
      }
    },
    [sort, onSortChange, internalSortKey],
  );

  // ── Sort data (client-side when no external sort handler) ──
  const sortedData = useMemo(() => {
    if (onSortChange) return data; // Server-side sort — don't re-sort locally
    if (!internalSortKey) return data;

    const col = columns.find((c) => String(c.key) === internalSortKey && c.sortable);
    if (!col) return data;

    return [...data].sort((a, b) => {
      const valA = col.sortValue
        ? col.sortValue(a)
        : (a as Record<string, unknown>)[internalSortKey];
      const valB = col.sortValue
        ? col.sortValue(b)
        : (b as Record<string, unknown>)[internalSortKey];

      if (typeof valA === 'string' && typeof valB === 'string') {
        return internalSortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      if (typeof valA === 'number' && typeof valB === 'number') {
        return internalSortDir === 'asc' ? valA - valB : valB - valA;
      }
      return 0;
    });
  }, [data, internalSortKey, internalSortDir, columns, onSortChange]);

  // ── Pagination ─────────────────────────────────────────────
  const pageCount = pagination
    ? Math.max(1, Math.ceil(pagination.totalItems / pagination.pageSize))
    : 1;

  // ── Toolbar rendering ──────────────────────────────────────
  const showToolbar = !!(
    toolbar?.showSearch ||
    toolbar?.showColumnToggle ||
    toolbar?.showFilters ||
    toolbar?.primaryAction ||
    toolbar?.rightContent ||
    toolbar?.filters
  );

  // ── Cell classes ────────────────────────────────────────────
  const thClass = isCompact
    ? 'px-2 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide text-left'
    : 'px-3 py-2.5 text-[12px] font-semibold text-muted-foreground uppercase tracking-wide text-left';

  const tdClass = isCompact
    ? 'px-2 py-1.5 text-[11px] text-foreground'
    : 'px-3 py-2.5 text-[12px] text-foreground';

  // ── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <div className={cn('table-shell flex flex-col', className)} data-testid={testId}>
        {showToolbar && (
          <AppTableToolbar
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={toolbar?.searchPlaceholder}
            showSearch={toolbar?.showSearch}
            showSettings={toolbar?.showColumnToggle}
            primaryAction={toolbar?.primaryAction}
            filters={toolbar?.filters}
            rightContent={toolbar?.rightContent}
          />
        )}
        <TableSkeleton
          rows={skeletonRows}
          columns={Math.max(visibleCols.length, 1)}
        />
      </div>
    );
  }

  return (
    <div className={cn('table-shell flex flex-col h-full', className)} data-testid={testId}>
      {showToolbar && (
        <AppTableToolbar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={toolbar?.searchPlaceholder}
          showSearch={toolbar?.showSearch}
          showSettings={toolbar?.showColumnToggle}
          primaryAction={toolbar?.primaryAction}
          filters={toolbar?.filters}
          rightContent={toolbar?.rightContent}
        />
      )}

      {/* Empty state */}
      {!error && sortedData.length === 0 && emptyContent ? (
        emptyContent
      ) : !error && sortedData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
          {emptyDescription && (
            <p className="mt-1 text-xs text-muted-foreground">{emptyDescription}</p>
          )}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-[12px] text-destructive font-medium">{error}</p>
        </div>
      ) : (
        <>
          {/* Card list — replaces the table below md: (768px) when cardView is
              configured. Table markup is untouched at md: and above. */}
          {cardView && (
            <div className="md:hidden flex flex-col gap-2.5 p-1">
              {sortedData.map((row, rowIndex) => (
                <DataGridCard
                  key={getRowKey(row, rowIndex)}
                  row={row}
                  rowIndex={rowIndex}
                  columns={visibleCols}
                  cardView={cardView}
                  onRowClick={onRowClick}
                  rowClassName={getRowClassName?.(row, rowIndex)}
                  expandedContent={
                    isRowExpanded?.(row) ? renderExpandedRow?.(row, rowIndex) : null
                  }
                />
              ))}
            </div>
          )}

          {/* Table */}
          <div className={cn('workspace-table-container flex-1', cardView && 'hidden md:block')}>
            <table
              className="app-data-table w-full"
              data-density={isCompact ? 'compact' : 'comfortable'}
            >
              <thead className={cn(stickyHeader && 'sticky top-0 z-10')}>
                <tr>
                  {visibleCols.map((col) => (
                    <th
                      key={String(col.key)}
                      onClick={col.sortable ? () => handleSort(String(col.key)) : undefined}
                      className={cn(
                        thClass,
                        col.align === 'center' && 'text-center',
                        col.align === 'right' && 'text-right',
                        col.sortable && 'cursor-pointer select-none hover:bg-muted/30',
                        col.hideOnMobile && 'hidden sm:table-cell',
                        col.headerClassName,
                      )}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-1',
                          col.align === 'right' && 'justify-end',
                        )}
                      >
                        {col.renderHeader ? col.renderHeader() : col.label}
                        {col.sortable && (
                          effectiveSort?.column === String(col.key) ? (
                            effectiveSort.direction === 'asc' ? (
                              <ArrowUp className="h-3 w-3 text-primary" />
                            ) : (
                              <ArrowDown className="h-3 w-3 text-primary" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-40" />
                          )
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, rowIndex) => {
                  const rowKey = getRowKey(row, rowIndex);
                  const expanded = !!(
                    isRowExpanded?.(row) && renderExpandedRow?.(row, rowIndex)
                  );
                  const customRowClass = getRowClassName?.(row, rowIndex);

                  return (
                    <Fragment key={rowKey}>
                      <tr
                        className={cn(
                          striped && 'even:bg-muted/20',
                          onRowClick && 'cursor-pointer',
                          customRowClass,
                        )}
                        onClick={
                          onRowClick
                            ? (e) => {
                                const target = e.target as HTMLElement;
                                if (
                                  target.closest('button') ||
                                  target.closest('a') ||
                                  target.closest('input') ||
                                  target.closest('[role="button"]') ||
                                  target.closest('[role="menu"]')
                                ) {
                                  return;
                                }
                                onRowClick(row, rowIndex);
                              }
                            : undefined
                        }
                      >
                        {visibleCols.map((col) => {
                          const rawValue = getCellValueFromRow(row, col.key);
                          const isColEditable = resolverContext
                            ? (col.editable ?? false) && !!onUpdate
                            : (col.editable ?? editable) && !!onUpdate;

                          let content: React.ReactNode;
                          if (col.render) {
                            content = col.render(row, rowIndex);
                          } else if (isColEditable) {
                            content = (
                              <DataGridEditableCell
                                value={rawValue}
                                row={row}
                                rowIndex={rowIndex}
                                column={col}
                                isEditable={isColEditable}
                                onSave={(newValue) => {
                                  onUpdate!({
                                    row,
                                    rowIndex,
                                    columnKey: col.key,
                                    oldValue: rawValue,
                                    newValue,
                                  });
                                }}
                                formatValue={formatCellValue}
                              />
                            );
                          } else {
                            content = formatCellValue(rawValue, col.type, col);
                          }

                          return (
                            <td
                              key={String(col.key)}
                              className={cn(
                                tdClass,
                                col.align === 'center' && 'text-center',
                                col.align === 'right' && 'text-right',
                                col.truncate !== false && 'truncate',
                                col.hideOnMobile && 'hidden sm:table-cell',
                                col.cellClassName,
                              )}
                              style={col.width ? { width: col.width } : undefined}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>

                      {expanded && (
                        <tr>
                          <td colSpan={visibleCols.length} className="p-0">
                            {renderExpandedRow!(row, rowIndex)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && sortedData.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-elevated px-4 py-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>
                  Page {pagination.page} of {pageCount}
                </span>
                <span>·</span>
                <span>{pagination.totalItems} total</span>
              </div>
              <div className="flex items-center gap-2">
                {pagination.onPageSizeChange && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Rows:</span>
                    <select
                      value={pagination.pageSize}
                      onChange={(e) => pagination.onPageSizeChange!(Number(e.target.value))}
                      className="h-6 cursor-pointer rounded-md border border-border bg-background px-1.5 py-0.5 text-xs text-foreground"
                    >
                      {(pagination.pageSizeOptions ?? [20, 50, 100]).map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange?.(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="h-6 gap-1 px-2 text-xs"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange?.(pagination.page + 1)}
                  disabled={pagination.page >= pageCount}
                  className="h-6 gap-1 px-2 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Utility ─────────────────────────────────────────────────

function getCellValueFromRow<TRow>(row: TRow, key: keyof TRow | string): unknown {
  if (typeof key === 'string' && key.includes('.')) {
    return key.split('.').reduce<unknown>((acc, part) => {
      if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[part];
      return undefined;
    }, row);
  }
  return (row as Record<string, unknown>)[key as string];
}

// ─── Export ──────────────────────────────────────────────────

export const DataGrid = memo(DataGridInner) as typeof DataGridInner;
